/**
 * Sync Queue Service
 * 
 * Manages offline-to-online synchronization for flows and custom nodes.
 * Queues operations when offline and syncs when connection is restored.
 */

import logger from '../utils/logger';

// Types for sync operations
export type SyncOperationType = 'save' | 'delete';
export type SyncEntityType = 'flow' | 'template' | 'customNode';

export interface SyncOperation {
  id: string;                    // Unique operation ID
  entityType: SyncEntityType;    // What type of entity
  entityId: string;              // ID of the entity being synced
  operation: SyncOperationType;  // save or delete
  data?: any;                    // The data to sync (for save operations)
  timestamp: number;             // When the operation was queued
  retryCount: number;            // Number of sync attempts
  lastError?: string;            // Last error message if any
}

export interface SyncStatus {
  isOnline: boolean;
  pendingCount: number;
  isSyncing: boolean;
  lastSyncTime: number | null;
  lastError: string | null;
}

type SyncStatusListener = (status: SyncStatus) => void;
type SyncHandler = (operation: SyncOperation) => Promise<void>;

const STORAGE_KEY = 'creative_nodeflow_sync_queue';
const MAX_RETRIES = 5;
const RETRY_DELAYS = [1000, 5000, 15000, 60000, 300000]; // 1s, 5s, 15s, 1m, 5m

class SyncQueueService {
  private queue: SyncOperation[] = [];
  private isOnline: boolean = navigator.onLine;
  private isSyncing: boolean = false;
  private lastSyncTime: number | null = null;
  private lastError: string | null = null;
  private listeners: Set<SyncStatusListener> = new Set();
  private syncHandlers: Map<SyncEntityType, SyncHandler> = new Map();
  private syncTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.loadQueue();
    this.setupOnlineListener();
    
    // Start sync if we have pending items and we're online
    if (this.isOnline && this.queue.length > 0) {
      this.scheduleSync(1000);
    }
  }

  /**
   * Load queue from localStorage
   */
  private loadQueue(): void {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        this.queue = JSON.parse(stored);
        logger.debug('[SyncQueue] Loaded queue with', this.queue.length, 'items');
      }
    } catch (error) {
      logger.error('[SyncQueue] Failed to load queue:', error);
      this.queue = [];
    }
  }

  /**
   * Save queue to localStorage
   */
  private saveQueue(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.queue));
    } catch (error) {
      logger.error('[SyncQueue] Failed to save queue:', error);
    }
  }

  /**
   * Setup online/offline event listeners
   */
  private setupOnlineListener(): void {
    window.addEventListener('online', () => {
      logger.info('[SyncQueue] Connection restored');
      this.isOnline = true;
      this.notifyListeners();
      this.scheduleSync(1000);
    });

    window.addEventListener('offline', () => {
      logger.info('[SyncQueue] Connection lost');
      this.isOnline = false;
      this.notifyListeners();
      if (this.syncTimeout) {
        clearTimeout(this.syncTimeout);
        this.syncTimeout = null;
      }
    });
  }

  /**
   * Schedule a sync attempt
   */
  private scheduleSync(delay: number): void {
    if (this.syncTimeout) {
      clearTimeout(this.syncTimeout);
    }
    this.syncTimeout = setTimeout(() => this.processQueue(), delay);
  }

  /**
   * Register a sync handler for an entity type
   */
  registerHandler(entityType: SyncEntityType, handler: SyncHandler): void {
    this.syncHandlers.set(entityType, handler);
    logger.debug('[SyncQueue] Registered handler for', entityType);
  }

  /**
   * Add an operation to the sync queue
   */
  enqueue(operation: Omit<SyncOperation, 'id' | 'timestamp' | 'retryCount'>): void {
    // Check if we already have a pending operation for this entity
    const existingIndex = this.queue.findIndex(
      op => op.entityType === operation.entityType && op.entityId === operation.entityId
    );

    const newOp: SyncOperation = {
      ...operation,
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      timestamp: Date.now(),
      retryCount: 0
    };

    if (existingIndex >= 0) {
      // Replace existing operation (newer operation supersedes)
      logger.debug('[SyncQueue] Replacing existing operation for', operation.entityId);
      this.queue[existingIndex] = newOp;
    } else {
      this.queue.push(newOp);
    }

    this.saveQueue();
    this.notifyListeners();
    
    // If we're online, try to sync immediately
    if (this.isOnline && !this.isSyncing) {
      this.scheduleSync(100);
    }
  }

  /**
   * Remove an operation from the queue
   */
  private dequeue(operationId: string): void {
    this.queue = this.queue.filter(op => op.id !== operationId);
    this.saveQueue();
    this.notifyListeners();
  }

  /**
   * Process the sync queue
   */
  async processQueue(): Promise<void> {
    if (!this.isOnline || this.isSyncing || this.queue.length === 0) {
      return;
    }

    this.isSyncing = true;
    this.notifyListeners();
    logger.info('[SyncQueue] Processing queue with', this.queue.length, 'items');

    const toProcess = [...this.queue];
    let successCount = 0;
    let failCount = 0;

    for (const operation of toProcess) {
      if (!this.isOnline) {
        logger.warn('[SyncQueue] Went offline during sync, stopping');
        break;
      }

      const handler = this.syncHandlers.get(operation.entityType);
      if (!handler) {
        logger.error('[SyncQueue] No handler for entity type:', operation.entityType);
        continue;
      }

      try {
        await handler(operation);
        this.dequeue(operation.id);
        successCount++;
        logger.debug('[SyncQueue] Synced operation:', operation.id);
      } catch (error) {
        failCount++;
        operation.retryCount++;
        operation.lastError = error instanceof Error ? error.message : String(error);
        
        if (operation.retryCount >= MAX_RETRIES) {
          logger.error('[SyncQueue] Max retries reached for:', operation.id, error);
          // Keep in queue but mark as failed - user might want to retry manually
        } else {
          logger.warn('[SyncQueue] Sync failed, will retry:', operation.id, error);
        }
        
        this.saveQueue();
      }
    }

    this.isSyncing = false;
    this.lastSyncTime = Date.now();
    this.lastError = failCount > 0 ? `${failCount} operation(s) failed to sync` : null;
    this.notifyListeners();

    logger.info('[SyncQueue] Sync complete:', { successCount, failCount, remaining: this.queue.length });

    // If there are still items to sync, schedule retry with backoff
    if (this.queue.length > 0 && this.isOnline) {
      const maxRetry = Math.max(...this.queue.map(op => op.retryCount));
      const delay = RETRY_DELAYS[Math.min(maxRetry, RETRY_DELAYS.length - 1)];
      this.scheduleSync(delay);
    }
  }

  /**
   * Get current sync status
   */
  getStatus(): SyncStatus {
    return {
      isOnline: this.isOnline,
      pendingCount: this.queue.length,
      isSyncing: this.isSyncing,
      lastSyncTime: this.lastSyncTime,
      lastError: this.lastError
    };
  }

  /**
   * Get pending operations (for debugging/UI)
   */
  getPendingOperations(): SyncOperation[] {
    return [...this.queue];
  }

  /**
   * Subscribe to status changes
   */
  subscribe(listener: SyncStatusListener): () => void {
    this.listeners.add(listener);
    // Immediately notify with current status
    listener(this.getStatus());
    
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Notify all listeners of status change
   */
  private notifyListeners(): void {
    const status = this.getStatus();
    this.listeners.forEach(listener => listener(status));
  }

  /**
   * Force a sync attempt (user-triggered)
   */
  forceSync(): void {
    if (this.isOnline && !this.isSyncing) {
      this.processQueue();
    }
  }

  /**
   * Clear all pending operations (dangerous - data loss)
   */
  clearQueue(): void {
    this.queue = [];
    this.saveQueue();
    this.lastError = null;
    this.notifyListeners();
    logger.warn('[SyncQueue] Queue cleared');
  }

  /**
   * Retry a specific failed operation
   */
  retryOperation(operationId: string): void {
    const operation = this.queue.find(op => op.id === operationId);
    if (operation) {
      operation.retryCount = 0;
      operation.lastError = undefined;
      this.saveQueue();
      this.notifyListeners();
      this.forceSync();
    }
  }

  /**
   * Remove a specific operation (user decided to discard)
   */
  discardOperation(operationId: string): void {
    this.dequeue(operationId);
    logger.info('[SyncQueue] Discarded operation:', operationId);
  }

  /**
   * Check if there are any pending operations for an entity
   */
  hasPendingOperation(entityType: SyncEntityType, entityId: string): boolean {
    return this.queue.some(
      op => op.entityType === entityType && op.entityId === entityId
    );
  }

  /**
   * Get the pending data for an entity (for offline reads)
   */
  getPendingData(entityType: SyncEntityType, entityId: string): any | null {
    const operation = this.queue.find(
      op => op.entityType === entityType && 
           op.entityId === entityId && 
           op.operation === 'save'
    );
    return operation?.data || null;
  }
}

// Singleton instance
export const syncQueueService = new SyncQueueService();
export default syncQueueService;
